import { format, parse } from 'date-fns'
import { AdvisorData, RoboAdvisorData } from '../../../utils/types'

// Wire contract for the eventual self-schedule booking backend integration.
export interface SelfScheduleBookingPayload {
  leadUserSessionId: string // required. matches existing String-on-the-wire convention
  advisorMatchId: number // required. which advisor card the user booked
  requestedStart: string // required. ISO-8601 with offset, e.g. "2026-06-12T09:30:00-04:00"
  timezone: string // required. IANA tz of the user's machine, e.g. "America/New_York"
}

export interface SelfScheduleBookingInput {
  leadUserSessionId: string
  advisorData: AdvisorData | RoboAdvisorData
  date: Date
  time: string
  timezone: string
}

// Combine the selected day with the slot time ("9:30 AM") into ISO-8601 with the
// machine's UTC offset, e.g. "2026-06-12T09:30:00-04:00".
const toRequestedStart = (date: Date, time: string): string =>
  format(parse(time, 'h:mm a', date), "yyyy-MM-dd'T'HH:mm:ssxxx")

export const buildSelfScheduleBookingPayload = (bookingInput: SelfScheduleBookingInput): SelfScheduleBookingPayload => ({
  leadUserSessionId: bookingInput.leadUserSessionId,
  advisorMatchId: bookingInput.advisorData.advisorMatchId,
  requestedStart: toRequestedStart(bookingInput.date, bookingInput.time),
  timezone: bookingInput.timezone
})

export interface SelfScheduleBookingResponse {
  success: boolean
}

// POSTs the booking to the lead-engine self-schedule endpoint. Resolves the endpoint's
// `{ success: true }` body on a 2xx response; throws on a non-OK status or transport error
// so the caller (the modal) can surface the error and skip the booked state.
export const submitSelfScheduleBooking = async (bookingInput: SelfScheduleBookingInput): Promise<SelfScheduleBookingResponse> => {
  const payload = buildSelfScheduleBookingPayload(bookingInput)

  const response = await fetch(leadEngineSelfScheduleUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  })

  if (!response.ok) {
    throw new Error(`Self-schedule booking failed with status ${response.status}`)
  }

  return response.json()
}
